import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Phone, Mail, MapPin, Check } from "lucide-react";
import Layout from "../components/layout/Layout";
import useSeo from "../hooks/useSeo";
import products from "../data/products";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const ORTER = ["Lidköping", "Götene", "Skara", "Annan ort"];

const PRODUCT_OPTIONS = [
  ...products.map((p) => ({ value: p.slug, label: p.category })),
  { value: "annat", label: "Annat" },
];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Swedish phone: allow +46 or 0-prefix, digits/spaces/hyphens, min 7 digits
const phoneRegex = /^[\d\s\-+()]{7,}$/;

const initialState = {
  namn: "",
  telefon: "",
  epost: "",
  ort: "",
  produkt: "",
  meddelande: "",
  gdpr: false,
};

const validate = (v) => {
  const errors = {};
  if (!v.namn.trim()) errors.namn = "Fyll i ditt namn.";
  if (!v.telefon.trim()) errors.telefon = "Fyll i ett telefonnummer.";
  else if (!phoneRegex.test(v.telefon.trim()))
    errors.telefon = "Ange ett giltigt telefonnummer.";
  if (!v.epost.trim()) errors.epost = "Fyll i en e-postadress.";
  else if (!emailRegex.test(v.epost.trim()))
    errors.epost = "Ange en giltig e-postadress.";
  if (!v.ort) errors.ort = "Välj en ort.";
  if (!v.produkt) errors.produkt = "Välj vad du är intresserad av.";
  if (!v.gdpr) errors.gdpr = "Du behöver godkänna för att kunna skicka.";
  return errors;
};

const Field = ({ id, label, error, required, children, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <Label
      htmlFor={id}
      className="text-sm font-medium text-[color:hsl(var(--ink))]"
    >
      {label}
      {required && (
        <span
          aria-hidden
          className="ml-1 text-[color:hsl(var(--accent-terracotta))]"
        >
          *
        </span>
      )}
    </Label>
    {children}
    {error && (
      <p
        data-testid={`${testid}-error`}
        className="text-xs text-[color:hsl(var(--accent-terracotta))] font-medium"
      >
        {error}
      </p>
    )}
  </div>
);

const inputClass = (hasError) =>
  `h-12 rounded-xl bg-white text-base md:text-base border ${
    hasError
      ? "border-[color:hsl(var(--accent-terracotta))] focus-visible:ring-[color:hsl(var(--accent-terracotta))]"
      : "border-[color:hsl(var(--line))] focus-visible:ring-[color:hsl(var(--accent-terracotta))]"
  }`;

const selectTriggerClass = (hasError) =>
  `h-12 rounded-xl bg-white text-base border ${
    hasError
      ? "border-[color:hsl(var(--accent-terracotta))]"
      : "border-[color:hsl(var(--line))]"
  }`;

export default function Kontakt() {
  useSeo({
    title: "Kontakt | Vido Klimatteknik",
    description:
      "Skicka din förfrågan till Vido Klimatteknik \u2014 fast pris, personlig kontakt och lokal installatör.",
    path: "/kontakt",
  });

  const [params] = useSearchParams();
  const preselect = params.get("produkt");

  const validPreselect = useMemo(
    () =>
      PRODUCT_OPTIONS.some((o) => o.value === preselect) ? preselect : "",
    [preselect],
  );

  const [values, setValues] = useState({
    ...initialState,
    produkt: validPreselect,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (validPreselect) {
      setValues((v) => ({ ...v, produkt: validPreselect }));
    }
  }, [validPreselect]);

  const setField = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    if (touched[name]) {
      setErrors(validate({ ...values, [name]: value }));
    }
  };

  const onBlur = (name) => {
    setTouched((t) => ({ ...t, [name]: true }));
    setErrors(validate(values));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const errs = validate(values);
    setErrors(errs);
    setTouched({
      namn: true,
      telefon: true,
      epost: true,
      ort: true,
      produkt: true,
      gdpr: true,
    });
    if (Object.keys(errs).length > 0) {
      const firstError = document.querySelector("[data-error-anchor]");
      if (firstError)
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    // Demo: no backend call. Simulate a small delay for feel.
    setTimeout(() => {
      setSubmitting(false);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 700);
  };

  return (
    <Layout>
      <section
        data-testid="kontakt-hero"
        className="pt-12 md:pt-16 pb-5 md:pb-6"
      >
        <div className="container-vido max-w-3xl fade-in">
          <span className="eyebrow">Kontakt</span>
          <h1 className="display-h1 mt-5">
            Skicka din{" "}
            <span className="italic text-[color:hsl(var(--accent-terracotta))]">
              förfrågan
            </span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[color:hsl(var(--muted-ink))] max-w-[58ch] leading-relaxed">
            Berätta kort vilken lösning du är intresserad av så återkommer
            Vido med bekräftelse och nästa steg.
          </p>
        </div>
      </section>

      <section
        data-testid="kontakt-body"
        className="pb-16 md:pb-20 pt-4 md:pt-6"
      >
        <div className="container-vido grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] max-w-5xl">
          {/* Form / Success */}
          <div>
            {success ? (
              <div
                data-testid="kontakt-success"
                className="card-vido p-8 md:p-12 fade-in is-visible"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[color:hsl(var(--accent-terracotta))]/12 text-[color:hsl(var(--accent-terracotta))]">
                  <Check size={22} />
                </div>
                <h2
                  className="mt-6 font-serif text-3xl md:text-4xl font-medium leading-tight"
                  data-testid="kontakt-success-title"
                >
                  Tack för din förfrågan
                </h2>
                <p className="mt-4 text-base md:text-lg text-[color:hsl(var(--muted-ink))] leading-relaxed max-w-[52ch]">
                  Din förfrågan har registrerats. Vido återkommer inom kort.
                </p>
                <p
                  data-testid="kontakt-success-demo-note"
                  className="mt-6 text-xs md:text-sm text-[color:hsl(var(--muted-ink))]/80 max-w-[52ch] leading-relaxed"
                >
                  Detta är en demo — inskicket sparas inte och skickas inte
                  vidare.
                </p>
                <div className="mt-8">
                  <Link
                    to="/"
                    data-testid="kontakt-success-cta"
                    className="btn-primary"
                  >
                    Tillbaka till startsidan
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            ) : (
              <form
                data-testid="kontakt-form"
                onSubmit={onSubmit}
                noValidate
                className="card-vido p-6 md:p-8 space-y-6"
              >
                <Field
                  id="namn"
                  label="Namn"
                  required
                  error={touched.namn && errors.namn}
                  testid="field-namn"
                >
                  <div data-error-anchor={touched.namn && errors.namn ? "1" : undefined}>
                    <Input
                      id="namn"
                      name="namn"
                      autoComplete="name"
                      data-testid="input-namn"
                      value={values.namn}
                      onChange={(e) => setField("namn", e.target.value)}
                      onBlur={() => onBlur("namn")}
                      className={inputClass(touched.namn && errors.namn)}
                    />
                  </div>
                </Field>

                <div className="grid gap-6 md:grid-cols-2">
                  <Field
                    id="telefon"
                    label="Telefon"
                    required
                    error={touched.telefon && errors.telefon}
                    testid="field-telefon"
                  >
                    <Input
                      id="telefon"
                      name="telefon"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="070-123 45 67"
                      data-testid="input-telefon"
                      value={values.telefon}
                      onChange={(e) => setField("telefon", e.target.value)}
                      onBlur={() => onBlur("telefon")}
                      className={inputClass(touched.telefon && errors.telefon)}
                    />
                  </Field>

                  <Field
                    id="epost"
                    label="E-post"
                    required
                    error={touched.epost && errors.epost}
                    testid="field-epost"
                  >
                    <Input
                      id="epost"
                      name="epost"
                      type="email"
                      autoComplete="email"
                      placeholder="namn@exempel.se"
                      data-testid="input-epost"
                      value={values.epost}
                      onChange={(e) => setField("epost", e.target.value)}
                      onBlur={() => onBlur("epost")}
                      className={inputClass(touched.epost && errors.epost)}
                    />
                  </Field>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <Field
                    id="ort"
                    label="Ort"
                    required
                    error={touched.ort && errors.ort}
                    testid="field-ort"
                  >
                    <Select
                      value={values.ort}
                      onValueChange={(v) => {
                        setField("ort", v);
                        setTouched((t) => ({ ...t, ort: true }));
                      }}
                    >
                      <SelectTrigger
                        id="ort"
                        data-testid="select-ort"
                        className={selectTriggerClass(touched.ort && errors.ort)}
                      >
                        <SelectValue placeholder="Välj ort" />
                      </SelectTrigger>
                      <SelectContent>
                        {ORTER.map((o) => (
                          <SelectItem
                            key={o}
                            value={o}
                            data-testid={`select-ort-${o.toLowerCase()}`}
                          >
                            {o}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field
                    id="produkt"
                    label="Intresserad av"
                    required
                    error={touched.produkt && errors.produkt}
                    testid="field-produkt"
                  >
                    <Select
                      value={values.produkt}
                      onValueChange={(v) => {
                        setField("produkt", v);
                        setTouched((t) => ({ ...t, produkt: true }));
                      }}
                    >
                      <SelectTrigger
                        id="produkt"
                        data-testid="select-produkt"
                        className={selectTriggerClass(
                          touched.produkt && errors.produkt,
                        )}
                      >
                        <SelectValue placeholder="Välj lösning" />
                      </SelectTrigger>
                      <SelectContent>
                        {PRODUCT_OPTIONS.map((o) => (
                          <SelectItem
                            key={o.value}
                            value={o.value}
                            data-testid={`select-produkt-${o.value}`}
                          >
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <Field
                  id="meddelande"
                  label="Meddelande"
                  testid="field-meddelande"
                >
                  <Textarea
                    id="meddelande"
                    name="meddelande"
                    rows={5}
                    placeholder="Berätta gärna kort om din fastighet, önskad tidsplan eller andra frågor."
                    data-testid="input-meddelande"
                    value={values.meddelande}
                    onChange={(e) => setField("meddelande", e.target.value)}
                    className="rounded-xl bg-white text-base border border-[color:hsl(var(--line))] focus-visible:ring-[color:hsl(var(--accent-terracotta))] min-h-[140px]"
                  />
                </Field>

                <div className="pt-2">
                  <div className="flex items-start gap-3" data-testid="field-gdpr">
                    <Checkbox
                      id="gdpr"
                      data-testid="checkbox-gdpr"
                      checked={values.gdpr}
                      onCheckedChange={(v) => {
                        setField("gdpr", Boolean(v));
                        setTouched((t) => ({ ...t, gdpr: true }));
                      }}
                      className="mt-1 h-5 w-5 rounded border-[color:hsl(var(--ink))]/40 data-[state=checked]:bg-[color:hsl(var(--accent-terracotta))] data-[state=checked]:border-[color:hsl(var(--accent-terracotta))]"
                    />
                    <label
                      htmlFor="gdpr"
                      className="text-sm text-[color:hsl(var(--muted-ink))] leading-relaxed cursor-pointer"
                    >
                      Jag samtycker till att Vido lagrar mina kontaktuppgifter
                      för att kunna återkomma om min förfrågan.
                    </label>
                  </div>
                  {touched.gdpr && errors.gdpr && (
                    <p
                      data-testid="field-gdpr-error"
                      className="mt-2 text-xs text-[color:hsl(var(--accent-terracotta))] font-medium"
                    >
                      {errors.gdpr}
                    </p>
                  )}
                </div>

                <div className="pt-3 flex flex-col items-start gap-3">
                  <button
                    type="submit"
                    data-testid="kontakt-submit"
                    disabled={submitting}
                    className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Skickar…" : "Skicka förfrågan"}
                    {!submitting && <ArrowRight size={18} />}
                  </button>
                  <span className="text-xs text-[color:hsl(var(--muted-ink))]/80 max-w-[52ch]">
                    Demo — inskicket sparas inte och skickas inte vidare.
                  </span>
                </div>
              </form>
            )}
          </div>

          {/* Sidebar */}
          <aside
            data-testid="kontakt-sidebar"
            className="lg:sticky lg:top-24 h-fit"
          >
            <div className="card-vido p-6 md:p-7">
              <span className="eyebrow">Hellre ringa?</span>
              <h3 className="mt-3 font-serif text-2xl font-medium leading-tight">
                Direkt kontakt med Vido
              </h3>
              <div className="mt-6 space-y-4">
                <a
                  href="tel:0793228187"
                  data-testid="sidebar-phone"
                  className="flex items-start gap-3 group"
                >
                  <Phone
                    size={16}
                    className="mt-1 flex-shrink-0 text-[color:hsl(var(--accent-terracotta))]"
                  />
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-[color:hsl(var(--muted-ink))]">
                      Telefon
                    </div>
                    <div className="text-base font-medium text-[color:hsl(var(--ink))] group-hover:text-[color:hsl(var(--accent-terracotta))] transition-colors">
                      079-322 81 87
                    </div>
                  </div>
                </a>
                <a
                  href="mailto:mikael@vido.nu"
                  data-testid="sidebar-email"
                  className="flex items-start gap-3 group"
                >
                  <Mail
                    size={16}
                    className="mt-1 flex-shrink-0 text-[color:hsl(var(--accent-terracotta))]"
                  />
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-[color:hsl(var(--muted-ink))]">
                      E-post
                    </div>
                    <div className="text-base font-medium text-[color:hsl(var(--ink))] group-hover:text-[color:hsl(var(--accent-terracotta))] transition-colors">
                      mikael@vido.nu
                    </div>
                  </div>
                </a>
                <div className="flex items-start gap-3">
                  <MapPin
                    size={16}
                    className="mt-1 flex-shrink-0 text-[color:hsl(var(--accent-terracotta))]"
                  />
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-[color:hsl(var(--muted-ink))]">
                      Serviceområde
                    </div>
                    <div className="text-base font-medium text-[color:hsl(var(--ink))]">
                      Lidköping · Götene · Skara
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}
